import { createStart, createMiddleware } from "@tanstack/react-start";

// NOTE: secret-guard is imported from src/server.ts (the Worker entry),
// NOT from here. start.ts is bundled into the client too — importing a
// *.server.ts module here drops its strings into the browser bundle and
// breaks hydration on the published site.
import { attachSupabaseAuth } from "@/integrations/supabase/auth-attacher";

import { renderErrorPage } from "./lib/error-page";


// ---- early startup env check ----
(() => {
  // Group env vars by purpose so logs show exactly what's missing.
  const groups: Array<{ name: string; vars: string[]; required: boolean }> = [
    {
      name: "Supabase (server)",
      vars: ["SUPABASE_URL", "SUPABASE_PUBLISHABLE_KEY", "SUPABASE_SERVICE_ROLE_KEY"],
      required: true,
    },
    {
      name: "Supabase (client/build)",
      vars: ["VITE_SUPABASE_URL", "VITE_SUPABASE_PUBLISHABLE_KEY", "VITE_SUPABASE_PROJECT_ID"],
      required: true,
    },
    { name: "OpenAI", vars: ["OPENAI_API_KEY"], required: false },
    { name: "Resend (email)", vars: ["RESEND_API_KEY"], required: false },
    { name: "TLS (Railway/Docker)", vars: ["SSL_CERT_FILE", "NODE_EXTRA_CA_CERTS"], required: false },
  ];

  const isLocalDev = !process.env.VITE_SUPABASE_URL && process.env.NODE_ENV !== "production";
  const runtime = process.env.RAILWAY_ENVIRONMENT
    ? `Railway (${process.env.RAILWAY_ENVIRONMENT})`
    : process.env.NODE_ENV === "production"
    ? "production"
    : "local-dev";

  const missingRequired: string[] = [];
  const lines: string[] = [];
  lines.push("=".repeat(76));
  lines.push(`  STARTUP DIAGNOSTICS — runtime: ${runtime}`);
  lines.push("=".repeat(76));

  for (const group of groups) {
    lines.push(`  [${group.name}]${group.required ? " (required)" : ""}`);
    for (const v of group.vars) {
      const present = Boolean(process.env[v]);
      const mark = present ? "OK  " : group.required ? "MISS" : "skip";
      lines.push(`    ${mark}  ${v}`);
      if (!present && group.required) missingRequired.push(v);
    }
  }
  lines.push("=".repeat(76));
  lines.push("  Probe runtime health:  GET /api/public/diag");
  lines.push("  Liveness check:        GET /api/public/health");
  lines.push("=".repeat(76));

  const logFn = missingRequired.length > 0 ? console.warn : console.log;
  for (const line of lines) logFn(line);

  if (missingRequired.length > 0) {
    console.warn(`  STARTUP WARNING: missing required env vars: ${missingRequired.join(", ")}`);
    if (isLocalDev) {
      console.warn("  Run:  bash scripts/setup-local-env.sh");
      console.warn("  Or add the variables to .dev.vars in the project root.");
    } else {
      console.warn("  Add the variables in your hosting dashboard (Railway → Variables)");
      console.warn("  and redeploy. Values are in Lovable Cloud → Backend → Settings → API.");
    }
  }
})();
// ---------------------------------


const errorMiddleware = createMiddleware().server(async ({ next }) => {
  try {
    return await next();
  } catch (error) {
    if (error != null && typeof error === "object" && "statusCode" in error) {
      throw error;
    }
    console.error(error);
    return new Response(renderErrorPage(), {
      status: 500,
      headers: { "content-type": "text/html; charset=utf-8" },
    });
  }
});

// Logs crawler / AI-bot hits to public.bot_visits. Runs after the response
// is produced so it never delays the user-facing request, and swallows all
// errors so logging failures cannot break the site.
const botLoggerMiddleware = createMiddleware().server(async ({ next, request }) => {
  const started = Date.now();
  const response = await next();
  try {
    const ua = request.headers.get("user-agent");
    const { detectBot, hashIp, logBotVisit } = await import("@/lib/bot-logger.server");
    const botName = detectBot(ua);
    if (!botName) return response;

    const url = new URL(request.url);
    // Skip asset/api noise — only log actual page navigations
    if (
      url.pathname.startsWith("/api/") ||
      url.pathname.startsWith("/_build/") ||
      url.pathname.startsWith("/assets/") ||
      /\.(?:js|css|png|jpe?g|webp|svg|ico|woff2?|map|txt|xml)$/i.test(url.pathname)
    ) {
      return response;
    }

    const ip =
      request.headers.get("cf-connecting-ip") ||
      request.headers.get("x-real-ip") ||
      request.headers.get("x-forwarded-for")?.split(",")[0]?.trim() ||
      null;

    // Fire-and-forget — do not await
    void logBotVisit({
      path: url.pathname,
      query: url.search || null,
      user_agent: ua,
      bot_name: botName,
      ip_hash: hashIp(ip),
      status_code: typeof (response as unknown as Response).status === "number" ? (response as unknown as Response).status : null,
      response_ms: Date.now() - started,
      referrer: request.headers.get("referer"),
    });
  } catch {
    /* never break user requests because of logging */
  }
  return response;
});

// Keep partner.whatfinland.com requests on the partner subdomain.
// - Rewrites bare paths into the /partner tree so SSR resolves the portal
//   routes (mirrors the Worker-level rewrite in src/server.ts for local dev
//   + any middleware that runs before it).
// - Blocks canonical/apex redirects: if a downstream response tries to send
//   the browser to https://whatfinland.com or https://www.whatfinland.com,
//   rewrite the Location header to stay on partner.whatfinland.com so the
//   partner portal never bounces users to the marketing site.
const partnerHostMiddleware = createMiddleware().server(async ({ next, request }) => {
  const url = new URL(request.url);
  const host = url.hostname.toLowerCase();
  const isPartner = host === "partner.whatfinland.com" || host.startsWith("partner.");

  const isTokenOnboarding = url.pathname === "/onboard" || url.pathname.startsWith("/onboard/");
  // /profile is the consumer account page and must render on any host,
  // including partner.whatfinland.com — the header account menu links here.
  const isConsumerProfile =
    url.pathname === "/profile" || url.pathname.startsWith("/profile/");
  const isConsumerOnboarding =
    url.pathname === "/onboarding" || url.pathname.startsWith("/onboarding/");
  if (
    isPartner &&
    !isTokenOnboarding &&
    !isConsumerProfile &&
    !url.pathname.startsWith("/partner")
  ) {
    const rewritten = new URL(url.toString());
    rewritten.pathname = isConsumerOnboarding
      ? "/partner/onboarding"
      : "/partner" + (url.pathname === "/" ? "" : url.pathname);
    request = new Request(rewritten.toString(), request);
  }

  const result = await next();
  if (!isPartner) return result;

  const response = (result as unknown as { response?: Response }).response ?? (result as unknown as Response);
  if (!(response instanceof Response)) return result;

  const location = response.headers.get("location");
  if (!location) return result;

  try {
    const target = new URL(location, url);
    const targetHost = target.hostname.toLowerCase();
    if (targetHost === "whatfinland.com" || targetHost === "www.whatfinland.com") {
      target.hostname = host;
      target.protocol = url.protocol;
      target.port = url.port;
      if (!target.pathname.startsWith("/partner")) {
        const targetIsConsumerProfile =
          target.pathname === "/profile" || target.pathname.startsWith("/profile/");
        const targetIsConsumerOnboarding =
          target.pathname === "/onboarding" || target.pathname.startsWith("/onboarding/");
        if (!targetIsConsumerProfile) {
          target.pathname = targetIsConsumerOnboarding
            ? "/partner/onboarding"
            : "/partner" + (target.pathname === "/" ? "" : target.pathname);
        }
      }
      const headers = new Headers(response.headers);
      headers.set("location", target.toString());
      const rewritten = new Response(response.body, {
        status: response.status,
        statusText: response.statusText,
        headers,
      });
      if ((result as unknown as { response?: Response }).response) {
        (result as unknown as { response: Response }).response = rewritten;
        return result;
      }
      return rewritten;
    }
  } catch {
    /* leave the response alone if the Location isn't parseable */
  }
  return result;
});

// Fallback bearer attacher: if `getSession()` came back empty (token expired or
// a refresh was in flight), force a refresh so protected server fns don't 401
// with "Unauthorized: No authorization header provided".
const ensureSupabaseAuth = createMiddleware({ type: "function" }).client(async ({ next }) => {
  if (typeof window === "undefined") return next();
  const { supabase } = await import("@/integrations/supabase/client");
  const { data } = await supabase.auth.getSession();
  let token = data.session?.access_token;
  if (!token) {
    const { data: refreshed } = await supabase.auth.refreshSession();
    token = refreshed.session?.access_token;
  }
  return next({ headers: token ? { Authorization: `Bearer ${token}` } : {} });
});

export const startInstance = createStart(() => ({
  requestMiddleware: [errorMiddleware, partnerHostMiddleware, botLoggerMiddleware],
  functionMiddleware: [attachSupabaseAuth, ensureSupabaseAuth],
}));


